import {
  Box,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
    scale: 0.98,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
  exit: (direction) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    scale: 0.98,
  }),
};

export const FastActionsSlider = ({ fastActions }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = isMobile ? 2 : 4;
  const totalPages = Math.ceil(fastActions.length / itemsPerPage);

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const handleNext = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
  };

  const visibleRows = (() => {
    const start = current * itemsPerPage;
    const end = start + itemsPerPage;
    const sliced = fastActions.slice(start, end);

    // Add placeholders if not enough items to fill the page
    while (sliced.length < itemsPerPage) {
      sliced.push(null);
    }
    return sliced;
  })();

  const motionKey = `${current}-${itemsPerPage}`;

  return (
    <Box width="100%" position="relative">
      <IconButton
        onClick={handlePrev}
        sx={{
          position: "absolute",
          left: -33,
          top: "40%",
          transform: "translateY(-50%)",
          zIndex: 1,
        }}
        aria-label="Anterior"
      >
        <ArrowBackIosNewIcon />
      </IconButton>

      <Box sx={{ width: "100%" }}>
        <Box
          minHeight={170}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          position="relative"
          width="100%"
          overflow="hidden"
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            gap={2}
            overflow="hidden"
          >
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={motionKey}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35 }}
                style={{
                  display: "flex",
                  gap: "16px",
                  width: "100%",
                  position: "absolute",
                  padding: "0 5px",
                }}
              >
                {visibleRows.map((row, index) =>
                  row ? (
                    <Paper
                      elevation={3}
                      component={"button"}
                      key={index}
                      sx={{
                        flex: 1,
                        width: "20%",
                        padding: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "none",
                        "&:hover": {
                          opacity: 0.6,
                        },
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        row?.onClick?.();
                      }}
                    >
                      <Box
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                        padding={2}
                      >
                        {row?.icon && row?.icon}
                        <Typography
                          fontWeight="bold"
                          fontSize={18}
                          textAlign="center"
                          title={row?.title}
                        >
                          {row?.title}
                        </Typography>
                      </Box>
                    </Paper>
                  ) : (
                    <Box
                      key={index}
                      sx={{
                        flex: 1,
                        width: "20%",
                        padding: 2,
                        background: "transparent",
                      }}
                    />
                  )
                )}
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>

      <IconButton
        onClick={handleNext}
        sx={{
          position: "absolute",
          right: -33,
          top: "40%",
          transform: "translateY(-50%)",
          zIndex: 1,
        }}
        aria-label="Próximo"
      >
        <ArrowForwardIosIcon />
      </IconButton>

      <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
        <Box
          mt={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
        >
          {Array.from({ length: totalPages }).map((_, idx) => (
            <Box
              key={idx}
              onClick={() => setCurrent(idx)}
              width={10}
              height={10}
              borderRadius="50%"
              bgcolor={idx === current ? "primary.main" : "grey.400"}
              sx={{
                cursor: "pointer",
                transition: "background 0.2s",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
